import React, { useState } from 'react';

import { cssMap } from '@compiled/react';

import { cx } from '@atlaskit/css';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import {
	type ActionItem,
	ActionName,
	Card,
	type ElementItem,
	ElementName,
	SmartLinkPosition,
	SmartLinkSize,
	SmartLinkTheme,
	TitleBlock,
} from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';

import { type LinkListVariantProps } from '../types';

const styles = cssMap({
	root: {
		borderLeftColor: token('color.border'),
		borderLeftWidth: token('border.width'),
		borderLeftStyle: 'solid',
		borderRightColor: token('color.border'),
		borderRightWidth: token('border.width'),
		borderRightStyle: 'solid',
		position: 'relative',
	},
	linkWrapperUnresolved: {
		minHeight: '56px',
	},
	unresolvedSkeletonOverlay: {
		position: 'absolute',
		inset: '0',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.medium'),
	},
	linkWrapper: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-block-title-resolved-view"]': {
			display: 'flex',
			alignItems: 'center',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-links-container"]': {
			paddingTop: token('space.100'),
			paddingRight: token('space.150'),
			paddingBottom: token('space.100'),
			paddingLeft: token('space.150'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& a[data-smart-element="Title"]': {
			font: token('font.body.small'),
			fontWeight: token('font.weight.semibold'),
			color: token('color.text'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-element-group"]': {
			maxWidth: '375px',
			gap: token('space.0'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-block-title-resolved-view"] .actions-button-group': {
			marginLeft: 'auto',
			alignSelf: 'center',
			display: 'flex',
			alignItems: 'center',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'span[data-smart-element="CreatedBy"]::before': {
			content: '•',
			marginRight: token('space.100'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'span[data-smart-element="ModifiedOn"]::before': {
			content: 'none',
			display: 'none',
		},
	},
	borderTop: {
		borderTopColor: token('color.border'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
	},
	borderTopRounded: {
		borderTopLeftRadius: token('radius.medium'),
		borderTopRightRadius: token('radius.medium'),
	},
	borderBottomRounded: {
		borderBottomColor: token('color.border'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomLeftRadius: token('radius.medium'),
		borderBottomRightRadius: token('radius.medium'),
	},
});

const actions: ActionItem[] = [
	{
		name: ActionName.PreviewAction,
		hideContent: true,
	},
];
const metadata: ElementItem[] = [
	{ name: ElementName.State },
	{ name: ElementName.AuthorGroup },
	{ name: ElementName.ModifiedOn },
];

const LinkListFullComponent = ({
	url,
	borderTopRounded,
	borderBottomRounded,
}: {
	borderBottomRounded: boolean;
	borderTopRounded: boolean;
	url: string;
}) => {
	const [resolved, setResolved] = useState(false);
	const handleResolve = () => setResolved(true);

	return (
		<Box
			xcss={cx(
				styles.root,
				styles.linkWrapper,
				styles.borderTop,
				borderTopRounded && styles.borderTopRounded,
				borderBottomRounded && styles.borderBottomRounded,
				!resolved && styles.linkWrapperUnresolved,
			)}
			testId="link-list-full-item"
		>
			<Card
				appearance="block"
				url={url}
				ui={{
					theme: SmartLinkTheme.Black,
					clickableContainer: true,
					hideBackground: true,
					hideElevation: true,
					hidePadding: true,
					size: SmartLinkSize.Medium,
					removeBlockRestriction: true,
				}}
				onResolve={handleResolve}
			>
				<TitleBlock
					maxLines={1}
					actions={actions}
					showActionOnHover
					metadata={metadata}
					subtitle={[
						{ name: ElementName.Provider, hideIcon: true },
						{ name: ElementName.CreatedBy },
					]}
					position={SmartLinkPosition.Center}
				/>
			</Card>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
			{!resolved && <Box xcss={styles.unresolvedSkeletonOverlay} style={{ zIndex: 10 }} />}
		</Box>
	);
};

export const LinkListFull = ({ visibleLinks, borderBottomRounded }: LinkListVariantProps): React.JSX.Element => {
	return (
		<Stack space="space.0">
			{visibleLinks.map((url, idx) => (
				<LinkListFullComponent
					key={`${url}=${idx}`}
					url={url}
					borderTopRounded={idx === 0}
					borderBottomRounded={idx === visibleLinks.length - 1 && borderBottomRounded}
				/>
			))}
		</Stack>
	);
};
